import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_LOYALTY_SERVICE } from '@/api/constant';
import CommonTable, { TableColumn } from '@/components/common/commonTable/CommonTable';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import { ISpinResponse } from '@/interfaces/SpinResponse.interface';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusSquare } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SpinStatesProp {
  isLoading: boolean;
  spins: ISpinResponse[];
  pageInfo: PageInfo;
  selectedSpins: Set<string | number>;
  showDeleteModel: boolean;
  spinToDelete: string | null;
}
const SpinWheels = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canCreate } = usePermissions();
  const [states, setStates] = useState<SpinStatesProp>({
    isLoading: false,
    spins: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedSpins: new Set(),
    showDeleteModel: false,
    spinToDelete: null,
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchSpinWheels = async () => {
    try {
      handleChangeState({ isLoading: true });
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_LOYALTY_SERVICE}${API_ENDPOINT.spin}?${query ? `${query}` : ''}`;
      const res = await callApi(PATH, { method: 'GET' });
      handleChangeState({
        spins: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.log('error in fetch spins wheel', error);
    }
  };

  useEffect(() => {
    fetchSpinWheels();
  }, [states.pageInfo.page, states.pageInfo.limit]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.spins);

  const handleDeleteWheel = async (wheelId: string) => {
    try {
      const PATH = `${API_URL_LOYALTY_SERVICE}${API_ENDPOINT.spin}/${wheelId}`;
      await callApi(PATH, { method: 'DELETE' });
      fetchSpinWheels();
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in delete wheel', error);
    }
  };

  const columns: TableColumn<ISpinResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      className: 'p-3',
    },
    {
      key: 'totalSpins',
      header: 'Total Spins',
      className: 'p-3',
    },
    {
      key: 'Items',
      header: 'Total Items',
      className: 'p-3',
      render: (item) => item.spinItems.length,
    },
  ];

  const spinWheelActionConfig: Record<string, ActionConfig<ISpinResponse>> = {
    eidt: {
      text: 'Edit',
      onClick: (item) => navigate(`${RoutesLink.addSpinWheel}/${item.id}`),
      permission: () => canEdit('daily spin'),
    },
    delete: {
      text: 'Delete',
      onClick: (item) => {
        handleChangeState({ showDeleteModel: true, spinToDelete: item.id });
      },
      permission: () => canDelete('daily spin'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(spinWheelActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  return (
    <Layout>
      <PageHeading
        text="Spin Wheels"
        button={
          canCreate('daily spin')
            ? {
              icon: <PlusSquare />,
              text: 'Add New',
              onClick: () => {
                navigate(RoutesLink.addSpinWheel);
              },
            }
            : undefined
        }
      />
      <Card>
        <CommonTable
          columns={columns}
          data={states.spins}
          showCheckbox={true}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Spin Wheels Available"
          loading={states.isLoading}
          actions={actions}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          show={states.showDeleteModel}
          onClose={() =>
            handleChangeState({ showDeleteModel: true, spinToDelete: null })
          }
          onConfirm={() => handleDeleteWheel(states.spinToDelete as string)}
        />
      )}
    </Layout>
  );
};

export default SpinWheels;
